import { isPlaceholder } from './tree';

// Stable keys for every spot a name can be written into. Starting teams key by
// team id (t1…tN) so byes that skip round one still show the same name; later
// rounds key by the match label, which is fixed for a given template.
export function slotKey(m, slot) {
  const id = slot ? m.team_b : m.team_a;
  if (id === 'BYE') return null;
  if (id && !isPlaceholder(id)) return `team:${id}`;
  return `slot:${m.match_label || m.id}:${slot ? 'b' : 'a'}`;
}

export const champKey = (title) => `champ:${title}`;

// Preview schedule with typed names in place; everything else stays a write-in line.
export function applyCustomNames(schedule, names) {
  return (schedule || []).map((m) => ({
    ...m,
    team_a_name: names[slotKey(m, 0)] || '',
    team_b_name: names[slotKey(m, 1)] || '',
  }));
}

// Starting teams in seed order: [{ key: 'team:t1', label: 'Team 1' }, …]
export function startingSlots(teams) {
  return (teams || []).map((t, i) => ({ key: `team:${t.id}`, label: t.name || `Team ${i + 1}` }));
}

const KEY = (id) => `sg_bracket_custom:${id}`;
export const EMPTY_CUSTOM = { active: false, names: {}, scores: {}, title: '', date: '', logo: '', shareId: '', sourceId: '', eventId: '' };

const REF = /^(W|L)_(.+)$/;

// Typed names plus Library scores. Winners (and losers) move into spots the
// organizer left blank, so a scored bracket fills itself in round by round.
export function applyCustomResults(schedule, names, scores = {}) {
  const ms = applyCustomNames(schedule, names);
  const done = new Map();
  [...ms].sort((a, b) => (a.round ?? 0) - (b.round ?? 0)).forEach((m) => {
    ['a', 'b'].forEach((s) => {
      const ref = REF.exec(m[`team_${s}`] || '');
      const r = ref && done.get(ref[2]);
      if (r && !m[`team_${s}_name`]) m[`team_${s}_name`] = r[ref[1]];
    });
    const sc = scores[m.match_label];
    if (!sc) return;
    m.score_a = sc.a;
    m.score_b = sc.b;
    if (sc.a === sc.b || !m.team_a_name || !m.team_b_name) return;
    const aWon = sc.a > sc.b;
    m.winner = aWon ? m.team_a : m.team_b;
    m.loser = aWon ? m.team_b : m.team_a;
    done.set(m.id, { W: aWon ? m.team_a_name : m.team_b_name, L: aWon ? m.team_b_name : m.team_a_name });
  });
  return ms;
}

export const bothNamed = (m) => !!(m.team_a_name && m.team_b_name);

// Names, title and date stay in this browser; the logo only for this browser session (uploaded only when shared).
export function loadCustom(templateId) {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY(templateId)) || 'null') || {};
    return { ...EMPTY_CUSTOM, ...saved, logo: sessionStorage.getItem(KEY(templateId)) || '' };
  } catch { return { ...EMPTY_CUSTOM }; }
}

export function saveCustom(templateId, { logo, ...data }) {
  try { localStorage.setItem(KEY(templateId), JSON.stringify(data)); } catch { /* storage full or private mode */ }
  try {
    if (logo) sessionStorage.setItem(KEY(templateId), logo);
    else sessionStorage.removeItem(KEY(templateId));
  } catch { /* storage full or private mode */ }
}

export const hasCustomContent = (c) => !!(c.title || c.date || c.logo || Object.values(c.names).some(Boolean) || Object.keys(c.scores || {}).length);

export function formatEventDate(iso) {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
}

// Logos are kept in the browser, scaled down so they print sharply but stay small.
export async function readLogo(file, max = 360) {
  const bmp = await createImageBitmap(file);
  const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * s);
  c.height = Math.round(bmp.height * s);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL('image/png');
}
